import { AiStreamFrame } from './ai-assistant-stream.models';
import { AiToolConsentDecision } from './ai-assistant.models';
import {
  AiErrorPartVm,
  AiMessageVm,
  AiPartKind,
  AiPartVm,
  AiTextPartVm,
  AiThinkingPartVm,
  AiToolCallPartVm,
} from './ai-assistant.view-models';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const newPartId = (): string => crypto.randomUUID();

export const normalizeStreamPartKind = (type: AiStreamFrame['type']): AiPartKind | undefined => {
  if (type === 'OUTPUT') return 'output';
  if (type === 'REASONING') return 'thinking';
  if (type === 'TOOL_CALL') return 'tool-call';
  return undefined;
};

const sameOptionalValue = (a: unknown, b: unknown): boolean => a === null || a === undefined || b === null || b === undefined || a === b;

export const shouldAppendToPart = (part: AiPartVm | undefined, frame: AiStreamFrame): boolean => {
  if (!part) return false;
  const kind = normalizeStreamPartKind(frame.type);
  if (!kind || part.kind !== kind) return false;
  if (part.kind === 'tool-call') {
    return (
      sameOptionalValue((part as AiToolCallPartVm).toolCallId, frame.toolCallId) && sameOptionalValue(part.id, frame.index?.toString())
    );
  }
  return sameOptionalValue((part as AiTextPartVm | AiThinkingPartVm).index?.toString(), frame.index?.toString());
};

export const isEmptyFrame = (frame: AiStreamFrame): boolean => {
  const isEmpty = (v: string | undefined | null): boolean => v === null || v === undefined || v === '';
  if (frame.type === 'OUTPUT') return isEmpty(frame.delta) && isEmpty(frame.content);
  if (frame.type === 'REASONING')
    return isEmpty(frame.delta) && isEmpty(frame.content) && isEmpty(frame.summaryDelta) && isEmpty(frame.summary);
  if (frame.type === 'TOOL_CALL')
    return isEmpty(frame.toolCallId) && isEmpty(frame.toolName) && isEmpty(frame.toolArgumentsDelta) && isEmpty(frame.toolArguments);
  return false;
};

export const reconcileCompleteText = (current: string, complete: string, replaceOnConflict = false): string => {
  if (!current || complete.startsWith(current)) return complete;
  if (current.endsWith(complete)) return current;
  return replaceOnConflict ? complete : current;
};

const shouldMarkPartComplete = (
  frameType: AiStreamFrame['frameType'],
  completeText: string | undefined,
  lastDelta: string | undefined
): boolean => frameType === 'COMPLETE' && !!completeText && completeText !== lastDelta;

// ---------------------------------------------------------------------------
// Part creation helpers
// ---------------------------------------------------------------------------

const createOutputPart = (frame: AiStreamFrame): AiTextPartVm => ({
  id: newPartId(),
  kind: 'output',
  complete: false,
  text: '',
  index: frame.index,
});

const createThinkingPart = (frame: AiStreamFrame): AiThinkingPartVm => ({
  id: newPartId(),
  kind: 'thinking',
  complete: false,
  text: '',
  index: frame.index,
});

const createToolCallPart = (frame: AiStreamFrame): AiToolCallPartVm => ({
  id: newPartId(),
  kind: 'tool-call',
  complete: false,
  toolCallId: frame.toolCallId,
  toolName: frame.toolName ?? '',
  toolArguments: '',
});

const createPartForFrame = (frame: AiStreamFrame): AiPartVm | undefined => {
  if (frame.type === 'OUTPUT') return createOutputPart(frame);
  if (frame.type === 'REASONING') return createThinkingPart(frame);
  if (frame.type === 'TOOL_CALL') return createToolCallPart(frame);
  return undefined;
};

// ---------------------------------------------------------------------------
// Part update helpers
// ---------------------------------------------------------------------------

const updateOutputPart = (part: AiTextPartVm, frame: AiStreamFrame): AiTextPartVm => {
  let text = part.text;
  let lastDelta = part.lastDelta;
  let complete = part.complete;

  if (frame.delta) {
    text += frame.delta;
    lastDelta = frame.delta;
  }
  if (frame.content) {
    text = reconcileCompleteText(text, frame.content);
  }
  if (shouldMarkPartComplete(frame.frameType, frame.content, lastDelta)) {
    complete = true;
  }

  return { ...part, text, lastDelta, complete };
};

const updateThinkingPart = (part: AiThinkingPartVm, frame: AiStreamFrame): AiThinkingPartVm => {
  let text = part.text;
  let lastDelta = part.lastDelta;
  let summary = part.summary;
  let lastSummaryDelta = part.lastSummaryDelta;
  let complete = part.complete;

  if (frame.delta) {
    text += frame.delta;
    lastDelta = frame.delta;
  }
  if (frame.content) {
    text = reconcileCompleteText(text, frame.content);
  }
  if (frame.summaryDelta) {
    summary = (summary ?? '') + frame.summaryDelta;
    lastSummaryDelta = frame.summaryDelta;
  }
  if (frame.summary) {
    summary = reconcileCompleteText(summary ?? '', frame.summary);
  }
  if (
    shouldMarkPartComplete(frame.frameType, frame.content, lastDelta) ||
    shouldMarkPartComplete(frame.frameType, frame.summary, lastSummaryDelta)
  ) {
    complete = true;
  }

  return { ...part, text, lastDelta, summary, lastSummaryDelta, complete };
};

const updateToolCallPart = (part: AiToolCallPartVm, frame: AiStreamFrame): AiToolCallPartVm => {
  let toolArguments = part.toolArguments;
  let lastToolArgumentsDelta = part.lastToolArgumentsDelta;
  let complete = part.complete;

  const toolCallId = frame.toolCallId ?? part.toolCallId;
  const toolName = frame.toolName ?? part.toolName;

  if (frame.toolArgumentsDelta) {
    toolArguments += frame.toolArgumentsDelta;
    lastToolArgumentsDelta = frame.toolArgumentsDelta;
  }
  if (frame.toolArguments) {
    toolArguments = reconcileCompleteText(toolArguments, frame.toolArguments, true);
  }
  if (shouldMarkPartComplete(frame.frameType, frame.toolArguments, lastToolArgumentsDelta)) {
    complete = true;
  }

  return { ...part, toolCallId, toolName, toolArguments, lastToolArgumentsDelta, complete };
};

const updatePart = (part: AiPartVm, frame: AiStreamFrame): AiPartVm => {
  if (part.kind === 'output') return updateOutputPart(part as AiTextPartVm, frame);
  if (part.kind === 'thinking') return updateThinkingPart(part as AiThinkingPartVm, frame);
  if (part.kind === 'tool-call') return updateToolCallPart(part as AiToolCallPartVm, frame);
  return part;
};

// ---------------------------------------------------------------------------
// Message helpers
// ---------------------------------------------------------------------------

const getOrCreateAssistantMessage = (messages: AiMessageVm[]): [AiMessageVm, boolean] => {
  const last = messages[messages.length - 1];
  if (last && last.role === 'assistant') return [last, false];
  const newMsg: AiMessageVm = { id: newPartId(), role: 'assistant', parts: [], timestamp: new Date().toISOString() };
  return [newMsg, true];
};

// ---------------------------------------------------------------------------
// Public API: apply a batch of frames to the current message list
// ---------------------------------------------------------------------------

export interface StreamAccumulatorResult {
  messages: AiMessageVm[];
  activePartId: string | undefined;
}

export const applyStreamFrames = (
  messages: AiMessageVm[],
  activePartId: string | undefined,
  frames: AiStreamFrame[]
): StreamAccumulatorResult => {
  let currentMessages = messages;
  let currentPartId = activePartId;
  for (const frame of frames) {
    const result = applyStreamFrame(currentMessages, currentPartId, frame);
    currentMessages = result.messages;
    currentPartId = result.activePartId;
  }
  return { messages: currentMessages, activePartId: currentPartId };
};

export const applyStreamFrame = (
  messages: AiMessageVm[],
  activePartId: string | undefined,
  frame: AiStreamFrame
): StreamAccumulatorResult => {
  // Ignore pure payload-less frames for kinds we accumulate
  if (isEmptyFrame(frame) && (frame.type === 'OUTPUT' || frame.type === 'REASONING' || frame.type === 'TOOL_CALL')) {
    return { messages, activePartId };
  }

  // Tool-call DELTA frames are transient streaming noise (partial JSON args) with no equivalent
  // in the conversation-history endpoint. Only the COMPLETE tool-call frame (full toolArguments)
  // becomes a durable part, keeping live rendering identical to history mode. Consent is attached
  // separately on RUN_WAITING_FOR_TOOL_CONSENT.
  if (frame.type === 'TOOL_CALL' && frame.frameType === 'DELTA') {
    return { messages, activePartId };
  }

  const kind = normalizeStreamPartKind(frame.type);
  if (!kind) {
    return { messages, activePartId };
  }

  const [assistantMsg, isNew] = getOrCreateAssistantMessage(messages);
  const activePart = assistantMsg.parts.find((p) => p.id === activePartId);

  let updatedParts: AiPartVm[];
  let newActivePartId: string | undefined;

  if (shouldAppendToPart(activePart, frame)) {
    // Update existing part
    updatedParts = assistantMsg.parts.map((p) => (p.id === activePartId ? updatePart(p, frame) : p));
    newActivePartId = activePartId;
  } else {
    // Finalize current part (mark complete) if there is one
    const finalizedParts = activePart
      ? assistantMsg.parts.map((p) => (p.id === activePartId ? { ...p, complete: true } : p))
      : assistantMsg.parts;

    const newPart = createPartForFrame(frame);
    if (!newPart) {
      return { messages, activePartId };
    }
    const partAfterFirstUpdate = updatePart(newPart, frame);
    updatedParts = [...finalizedParts, partAfterFirstUpdate];
    newActivePartId = partAfterFirstUpdate.id;
  }

  const updatedMsg: AiMessageVm = { ...assistantMsg, parts: updatedParts };
  const updatedMessages = isNew ? [...messages, updatedMsg] : messages.map((m) => (m.id === assistantMsg.id ? updatedMsg : m));

  return { messages: updatedMessages, activePartId: newActivePartId };
};

// ---------------------------------------------------------------------------
// Attach consent to a tool-call part (called on RUN_WAITING_FOR_TOOL_CONSENT)
// ---------------------------------------------------------------------------

export const attachConsentToToolCallPart = (
  messages: AiMessageVm[],
  activePartId: string | undefined,
  consent: { id: string; toolCallId?: string; toolName: string; toolArguments: string }
): AiMessageVm[] => {
  // Find existing tool-call part by toolCallId match
  const allParts = messages.flatMap((m) => m.parts);
  const existingPart = allParts.find(
    (p): p is AiToolCallPartVm =>
      p.kind === 'tool-call' && (!consent.toolCallId || (p as AiToolCallPartVm).toolCallId === consent.toolCallId)
  );

  if (existingPart) {
    return messages.map((m) => ({
      ...m,
      parts: m.parts.map((p) =>
        p.id === existingPart.id
          ? ({
              ...p,
              toolCallId: consent.toolCallId ?? (p as AiToolCallPartVm).toolCallId,
              toolName: consent.toolName,
              toolArguments: consent.toolArguments,
              pendingConsentId: consent.id,
              complete: true,
            } as AiToolCallPartVm)
          : p
      ),
    }));
  }

  // No matching part found — create one from the consent data
  const newPart: AiToolCallPartVm = {
    id: newPartId(),
    kind: 'tool-call',
    complete: true,
    toolCallId: consent.toolCallId,
    toolName: consent.toolName,
    toolArguments: consent.toolArguments,
    pendingConsentId: consent.id,
  };

  const [assistantMsg, isNew] = getOrCreateAssistantMessage(messages);
  const updatedMsg: AiMessageVm = { ...assistantMsg, parts: [...assistantMsg.parts, newPart] };
  return isNew ? [...messages, updatedMsg] : messages.map((m) => (m.id === assistantMsg.id ? updatedMsg : m));
};

// ---------------------------------------------------------------------------
// Stamp a consent decision onto the matching tool-call part (called on sendConsentDecision)
// ---------------------------------------------------------------------------

export const applyConsentDecisionToToolCallPart = (
  messages: AiMessageVm[],
  consentId: string,
  decision: AiToolConsentDecision
): AiMessageVm[] =>
  messages.map((m) => ({
    ...m,
    parts: m.parts.map((p) => {
      if (p.kind !== 'tool-call') return p;
      const toolCallPart = p as AiToolCallPartVm;
      if (toolCallPart.pendingConsentId !== consentId) return p;
      return {
        ...toolCallPart,
        consentDecision: decision,
        pendingConsentId: undefined,
      } as AiToolCallPartVm;
    }),
  }));

// ---------------------------------------------------------------------------
// Finalize the active part (called on DONE)
// ---------------------------------------------------------------------------

export const finalizeActivePart = (messages: AiMessageVm[], activePartId: string | undefined): AiMessageVm[] => {
  if (!activePartId) return messages;
  return messages.map((m) => ({
    ...m,
    parts: m.parts.map((p) => (p.id === activePartId ? { ...p, complete: true } : p)),
  }));
};

// ---------------------------------------------------------------------------
// Append an error part (called on ERROR stream frame)
// ---------------------------------------------------------------------------

export const appendErrorPart = (messages: AiMessageVm[], errorMessage: string): AiMessageVm[] => {
  const errorPart: AiErrorPartVm = {
    id: newPartId(),
    kind: 'error',
    complete: true,
    message: errorMessage,
  };
  const [assistantMsg, isNew] = getOrCreateAssistantMessage(messages);
  const updatedMsg: AiMessageVm = { ...assistantMsg, parts: [...assistantMsg.parts, errorPart] };
  return isNew ? [...messages, updatedMsg] : messages.map((m) => (m.id === assistantMsg.id ? updatedMsg : m));
};
